import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Droplet, Waves, Package, Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Home() {
  const categories = [
    {
      id: 1,
      name: "Filters",
      icon: Droplet,
      count: 8,
      description: "Zuiveringsfilters voor schoon water",
    },
    {
      id: 2,
      name: "Kranen & Kasten",
      icon: Waves,
      count: 12,
      description: "Waterkranen en opslagsystemen",
    },
    {
      id: 3,
      name: "Accessoires",
      icon: Package,
      count: 15,
      description: "Slangen, montagesets en meer",
    },
  ];

  const featuredProducts = [
    {
      id: 1,
      name: "RO Zuiveringsfilter",
      price: "€249,99",
      image: "🔵",
      rating: 4.8,
      category: "Filter",
    },
    {
      id: 2,
      name: "Koolstof Waterfilter",
      price: "€79,99",
      image: "⚫",
      rating: 4.6,
      category: "Filter",
    },
    {
      id: 3,
      name: "Premium Waterkraan",
      price: "€189,99",
      image: "💧",
      rating: 4.9,
      category: "Kraan",
    },
    {
      id: 4,
      name: "Montage Kit Compleet",
      price: "€39,99",
      image: "🔧",
      rating: 4.7,
      category: "Accessoire",
    },
  ];

  return (
    <main className="min-h-screen bg-gradient-to-b from-primary/5 to-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Droplet className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold text-foreground">WaterZuiver</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/shop" className="text-sm font-medium hover:text-primary transition-colors">
              Winkel
            </Link>
            <Button variant="outline" size="sm">
              0 items
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-4">
          Schoon & Gezond <span className="text-primary">Drinkwater</span>
        </h1>
        <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
          Professionele water zuiveringsfilterset. Premium filters, kranen en accessoires voor de beste waterkwaliteit thuis.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/shop">
            <Button size="lg" className="gap-2">
              Shop Nu <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <Button variant="outline" size="lg">
            Meer Info
          </Button>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold mb-12 text-center">Onze Categoriëen</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <Card key={cat.id} className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <Icon className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-2">{cat.name}</h3>
                <p className="text-muted-foreground mb-4">{cat.description}</p>
                <p className="text-sm text-primary font-semibold">{cat.count} producten</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold mb-4">Populaire Producten</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {featuredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-40 bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center text-4xl">
                {product.image}
              </div>
              <div className="p-4">
                <p className="text-xs text-primary font-semibold mb-1">{product.category}</p>
                <h3 className="font-semibold text-foreground mb-2">{product.name}</h3>
                <div className="flex items-center gap-1 mb-3">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3 h-3 ${i < Math.floor(product.rating) ? "fill-accent text-accent" : "text-muted"}`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground">{product.rating}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-primary">{product.price}</span>
                  <Button size="sm" variant="outline">
                    +
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
        <div className="text-center">
          <Link to="/shop">
            <Button variant="outline" size="lg">
              Zie Alle Producten
            </Button>
          </Link>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-primary/5 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Waarom WaterZuiver?</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: "✓", title: "Premium Kwaliteit", desc: "Certificeerde filters" },
              { icon: "🚚", title: "Snelle Levering", desc: "1-2 dagen verzending" },
              { icon: "💰", title: "Beste Prijs", desc: "Garantie laagste prijs" },
              { icon: "🛡️", title: "2 Jaar Garantie", desc: "Volledige garantie" },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl mb-3">{benefit.icon}</div>
                <h3 className="font-bold mb-1">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground/5 border-t py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-muted-foreground text-sm">
          <p>© 2026 WaterZuiver. Alle rechten voorbehouden.</p>
        </div>
      </footer>
    </main>
  );
}